import '@kanbios/ui/styles.css';
import { StrictMode, useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useRouteError } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { addMessages, Button, Dialog, I18nProvider, Skeleton, StatusPage, t, Toaster } from '@kanbios/ui';
import messages from './i18n/fr.json';
import { ApiFailure } from './lib/api';
import { goToSignIn, SessionProvider } from './lib/session';
import { Shell } from './components/Shell';
import { RefusalPage } from './components/Refusal';
import { SignInPage } from './pages/SignInPage';
import { AvailabilityPage } from './pages/AvailabilityPage';
import { AlertsPage } from './pages/AlertsPage';
import { ServersPage } from './pages/ServersPage';
import { ServerPage } from './pages/ServerPage';
import { ImportPage } from './pages/ImportPage';
import { DeclareApplicationPage } from './pages/DeclareApplicationPage';
import { InstallProjectPage } from './pages/InstallProjectPage';
import { EnvironmentPage } from './pages/EnvironmentPage';
import { NewProjectPage } from './pages/inventory/NewProjectPage';
import { RoadmapPage } from './pages/roadmap/RoadmapPage';
import { InfrastructurePage } from './pages/infrastructure/InfrastructurePage';
import { HomePage } from './pages/DashboardPage';
import { ChannelsPage } from './pages/settings/ChannelsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ApplicationsPage } from './pages/inventory/ApplicationsPage';
import { ProjectsPage } from './pages/inventory/ProjectsPage';
import { ClientsPage } from './pages/inventory/ClientsPage';
import { DomainsPage } from './pages/inventory/DomainsPage';
import { AddressesPage } from './pages/inventory/AddressesPage';
import { ProjectPage } from './pages/inventory/ProjectPage';
import { ClientPage } from './pages/inventory/ClientPage';
import { ProjectServersPage } from './pages/inventory/ProjectServersPage';
import { MonitoringRedirect } from './pages/inventory/MonitoringPage';
import { ApplicationPage } from './pages/inventory/ApplicationPage';
import { AuditPage } from './pages/reports/AuditPage';

// Every visible string lives in the i18n catalog (ADR-019).
addMessages('fr', messages);

// Session expiry is global: any call answering SESSION_EXPIRED opens the dialog.
let expired = false;
const listeners = new Set<() => void>();
const onFailure = (error: unknown) => {
  if (error instanceof ApiFailure && error.body.code === 'SESSION_EXPIRED' && !expired) {
    expired = true;
    listeners.forEach(l => l());
  } else if (error instanceof ApiFailure && error.body.code === 'UNAUTHENTICATED') {
    goToSignIn();
  }
};
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onFailure }),
  mutationCache: new MutationCache({ onError: onFailure }),
  defaultOptions: { queries: { retry: (count, error) => !(error instanceof ApiFailure && error.status < 500) && count < 2 } },
});

function SessionExpiredDialog() {
  const isExpired = useSyncExternalStore(cb => { listeners.add(cb); return () => listeners.delete(cb); }, () => expired);
  return (
    <Dialog
      open={isExpired}
      dismissible={false}
      title={t('session.expired.title')}
      description={t('session.expired.body')}
      confirmLabel={t('session.expired.reconnect')}
      onConfirm={() => goToSignIn('expired')}
      footer={undefined}
    />
  );
}

/** D27: Clients › Domaines moved to Domaines › Noms de domaine; links already sent keep working. */
function LegacyDomains() {
  const { search } = useLocation();
  return <Navigate to={`/domains/names${search}`} replace />;
}

function RouteError() {
  const error = useRouteError();
  return <RefusalPage error={error} />;
}

const signedIn = (element: JSX.Element) => <SessionProvider fallback={<Skeleton lines={6} />}>{element}</SessionProvider>;

const router = createBrowserRouter([
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/', element: signedIn(<HomePage />), errorElement: <RouteError /> },
  { path: '/servers', element: signedIn(<ServersPage list="monitored" />), errorElement: <RouteError /> },
  // D72: every cause, server and application, the gravest first.
  { path: '/alerts', element: signedIn(<AlertsPage />), errorElement: <RouteError /> },
  // D42: the servers not supervised yet, their own entry of the menu.
  { path: '/availability', element: signedIn(<AvailabilityPage />), errorElement: <RouteError /> },
  { path: '/pending-servers', element: signedIn(<ServersPage key="pending" list="pending" />), errorElement: <RouteError /> },
  { path: '/reserve-servers', element: signedIn(<ServersPage key="reserve" list="reserve" />), errorElement: <RouteError /> },
  // Wizard page type: outside the shell (SPEC-007).
  { path: '/servers/import', element: signedIn(<ImportPage />), errorElement: <RouteError /> },
  { path: '/servers/:server', element: signedIn(<ServerPage />), errorElement: <RouteError /> },
  { path: '/servers/:server/declare', element: signedIn(<DeclareApplicationPage />), errorElement: <RouteError /> },
  // SPEC-034 (lot 1.5): « Installer un projet » on a reserve server.
  { path: '/servers/:server/install', element: signedIn(<InstallProjectPage />), errorElement: <RouteError /> },
  // SPEC-034 § 9 and § 12 (lot 1.5): an environment the platform installed, and its operation.
  // D94: the environments are a tab of their project; the former list leads to « Projets ».
  { path: '/environments', element: <Navigate to="/projects" replace />, errorElement: <RouteError /> },
  { path: '/environments/:deployment', element: signedIn(<EnvironmentPage />), errorElement: <RouteError /> },
  { path: '/applications', element: signedIn(<ApplicationsPage />), errorElement: <RouteError /> },
  // SPEC-033 (D51): the application page; its former supervision address opens the Supervision tab.
  { path: '/applications/:application', element: signedIn(<ApplicationPage />), errorElement: <RouteError /> },
  { path: '/applications/:application/monitoring', element: <MonitoringRedirect />, errorElement: <RouteError /> },
  { path: '/projects', element: signedIn(<ProjectsPage />), errorElement: <RouteError /> },
  { path: '/roadmap', element: signedIn(<RoadmapPage />), errorElement: <RouteError /> },
  { path: '/infrastructure', element: signedIn(<InfrastructurePage />), errorElement: <RouteError /> },
  { path: '/projects/new', element: signedIn(<NewProjectPage />), errorElement: <RouteError /> },
  { path: '/projects/:project/servers', element: signedIn(<ProjectServersPage />), errorElement: <RouteError /> },
  { path: '/projects/:project', element: signedIn(<ProjectPage />), errorElement: <RouteError /> },
  { path: '/clients', element: signedIn(<ClientsPage />), errorElement: <RouteError /> },
  // D27: the domains have their own entry; the former address keeps working.
  { path: '/domains', element: signedIn(<AddressesPage />), errorElement: <RouteError /> },
  { path: '/domains/names', element: signedIn(<DomainsPage />), errorElement: <RouteError /> },
  { path: '/clients/domains', element: <LegacyDomains />, errorElement: <RouteError /> },
  { path: '/clients/:client', element: signedIn(<ClientPage />), errorElement: <RouteError /> },
  { path: '/reports/audit', element: signedIn(<AuditPage />), errorElement: <RouteError /> },
  { path: '/settings', element: signedIn(<SettingsPage />), errorElement: <RouteError /> },
  { path: '/settings/channels', element: signedIn(<ChannelsPage />), errorElement: <RouteError /> },
  {
    path: '*',
    element: signedIn(
      <Shell crumbs={[]}>
        <StatusPage status="404" title={t('errors.notFound.title')} actions={<Button variant="primary" onClick={() => window.location.assign('/')}>{t('errors.backToDashboard')}</Button>} />
      </Shell>,
    ),
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nProvider locale="fr">
      <QueryClientProvider client={queryClient}>
        <div className="kui">
          <RouterProvider router={router} />
          <SessionExpiredDialog />
          <Toaster />
        </div>
      </QueryClientProvider>
    </I18nProvider>
  </StrictMode>,
);
